import { Link } from 'react-router'
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  ArcElement, Filler, Tooltip, Legend,
  type ChartOptions,
} from 'chart.js'
import { Bar, Line, Doughnut } from 'react-chartjs-2'
import { kaufplan } from '../data/kaufplan'
import type { Einstieg, Urteil } from '../data/kaufplan.types'
import { useChartTokens, type ChartTokens } from '../hooks/useChartTokens'
import { URTEIL_STYLE } from '../components/urteil'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Filler, Tooltip, Legend)

function Section({ id, kicker, title, children, lead }: { id: string; kicker: string; title: string; lead?: string; children: React.ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="mt-14">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-mono text-[10px] font-bold tracking-[0.18em] text-accent">{kicker}</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      </div>
      <h2 id={`${id}-h`} className="text-[22px] font-semibold text-t1 tracking-tight">{title}</h2>
      {lead && <p className="text-[14px] text-t2 mt-1.5 max-w-[760px] leading-relaxed">{lead}</p>}
      <div className="mt-5">{children}</div>
    </section>
  )
}

function ChartCard({ title, source, children, height = 280, legend }: { title: string; source: string; children: React.ReactNode; height?: number; legend?: React.ReactNode }) {
  return (
    <figure className="card p-4 sm:p-5 m-0 flex flex-col">
      <figcaption className="text-[13px] font-semibold text-t1 mb-3">{title}</figcaption>
      <div style={{ height }} className="relative">{children}</div>
      {legend}
      <p className="font-mono text-[10px] text-t3 mt-3">Quelle: {source}</p>
    </figure>
  )
}

const pct = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`

// ── Chart: Tranchen-Leiter auf dem S&P 500 ─────────────────────────────────
function TranchenChart({ t }: { t: ChartTokens }) {
  const levels = kaufplan.tranchen.filter(x => x.spxLevel !== null)
  const labels = ['Hoch', 'Aktuell', ...levels.map(x => `T${x.nr}`)]
  const values = [kaufplan.spxHoch, kaufplan.spxAktuell, ...levels.map(x => x.spxLevel as number)]
  const colors = [t.text3, t.accent, ...levels.map(x => x.status === 'jetzt' || x.status === 'ausgelöst' ? t.pos : t.s1)]
  const min = Math.floor(Math.min(...values) * 0.97 / 100) * 100
  const options: ChartOptions<'bar'> = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: {
        label: c => {
          const v = c.parsed.y ?? 0
          const d = (v / kaufplan.spxHoch - 1) * 100
          return ` S&P 500 ${v.toLocaleString('de-CH')} (${pct(d)} vom Hoch)`
        },
      } },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
      y: { min, grid: { color: t.grid }, border: { display: false },
           ticks: { color: t.text3, font: { size: 11 }, callback: v => Number(v).toLocaleString('de-CH') } },
    },
  }
  return <Bar aria-label="Balkendiagramm: S&P-500-Level je Kauftranche im Vergleich zu Hoch und aktuellem Stand" role="img" options={options} data={{ labels, datasets: [{ data: values, backgroundColor: colors, borderRadius: 4, borderSkipped: 'bottom', barPercentage: 0.6 }] }} />
}

// ── Chart: Abstand vom Hoch je Markt ────────────────────────────────────────
function DrawdownChart({ t, rows }: { t: ChartTokens; rows: Einstieg[] }) {
  const sorted = rows.filter(r => r.vomHoch !== null).sort((a, b) => (a.vomHoch ?? 0) - (b.vomHoch ?? 0))
  const color = (u: Urteil) => ({ kaufen: t.pos, staffeln: t.s1, warten: t.warn, meiden: t.neg })[u]
  const options: ChartOptions<'bar'> = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: {
        label: c => {
          const r = sorted[c.dataIndex]
          return ` ${r.ticker}: ${pct(c.parsed.x ?? 0)} vom 52W-Hoch · ${URTEIL_STYLE[r.urteil].label}`
        },
      } },
    },
    scales: {
      x: { max: 0, grid: { color: t.grid }, border: { display: false },
           ticks: { color: t.text3, font: { size: 11 }, callback: v => `${v}%` } },
      y: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
    },
  }
  return <Bar aria-label="Balkendiagramm: Abstand der ETFs vom 52-Wochen-Hoch in Prozent, eingefärbt nach Urteil" role="img" options={options} data={{
    labels: sorted.map(r => r.name),
    datasets: [{ data: sorted.map(r => r.vomHoch), backgroundColor: sorted.map(r => color(r.urteil)), borderRadius: 4, borderSkipped: 'right', barThickness: 14 }],
  }} />
}

// ── Chart: 12-Monats-Rendite nach Stress-Ereignissen ───────────────────────
function ForwardChart({ t }: { t: ChartTokens }) {
  const d = kaufplan.nachKorrektur.daten
  const options: ChartOptions<'bar'> = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: {
        label: c => ` Ø ${pct(c.parsed.x ?? 0)} nach 12 Monaten · ${d[c.dataIndex].positiv12}% positiv`,
        afterLabel: c => ` Quelle: ${d[c.dataIndex].quelle}`,
      } },
    },
    scales: {
      x: { min: 0, grid: { color: t.grid }, border: { display: false }, ticks: { color: t.text3, font: { size: 11 }, callback: v => `${v}%` } },
      y: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
    },
  }
  return <Bar aria-label="Balkendiagramm: durchschnittliche 12-Monats-Rendite des S&P 500 nach Stress-Ereignissen" role="img" options={options} data={{
    labels: d.map(x => x.label),
    datasets: [{ data: d.map(x => x.rendite12), backgroundColor: d.map(x => x.label.startsWith('Basis') ? t.text3 : t.s1), borderRadius: 4, borderSkipped: 'left', barThickness: 18 }],
  }} />
}

// ── Chart: Saisonalität ─────────────────────────────────────────────────────
function SaisonChart({ t }: { t: ChartTokens }) {
  const d = kaufplan.saison.daten
  const options: ChartOptions<'bar'> = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => ` Ø ${pct(c.parsed.y ?? 0)}` } } },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
      y: { grid: { color: t.grid }, border: { display: false }, ticks: { color: t.text3, font: { size: 11 }, callback: v => `${v}%` } },
    },
  }
  return <Bar aria-label="Balkendiagramm: durchschnittliche Monatsrendite des S&P 500 von 1950 bis 2025" role="img" options={options} data={{
    labels: d.map(x => x.monat),
    datasets: [{ data: d.map(x => x.wert), backgroundColor: d.map(x => x.wert >= 0 ? t.pos : t.neg), borderRadius: 4, borderSkipped: 'bottom' }],
  }} />
}

// ── Chart: Equity Risk Premium ──────────────────────────────────────────────
function ErpChart({ t }: { t: ChartTokens }) {
  const d = kaufplan.erp.daten
  const options: ChartOptions<'line'> = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => ` ERP ${(c.parsed.y ?? 0).toFixed(2)} Pp.` } } },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
      y: { grid: { color: t.grid }, border: { display: false }, ticks: { color: t.text3, font: { size: 11 }, callback: v => `${v}` } },
    },
  }
  return <Line aria-label="Liniendiagramm: Equity Risk Premium von 2000 bis Oktober 2026" role="img" options={options} data={{
    labels: d.map(x => x.label),
    datasets: [{
      data: d.map(x => x.wert), borderColor: t.s1, borderWidth: 2, tension: 0.3,
      pointRadius: 4, pointBackgroundColor: d.map(x => x.wert < 0 ? t.neg : t.s1), pointBorderColor: t.surface, pointBorderWidth: 2,
      fill: { target: { value: 0 }, above: `${t.s1}22`, below: `${t.neg}33` },
    }],
  }} />
}

// ── Chart: Musterportfolio ──────────────────────────────────────────────────
function AllokationChart({ t }: { t: ChartTokens }) {
  const colors = [t.s1, t.s2, t.s3, t.s4, t.s5, t.s7]
  const a = kaufplan.allokation
  const options: ChartOptions<'doughnut'> = {
    responsive: true, maintainAspectRatio: false, cutout: '62%',
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => ` ${c.label}: ${c.parsed}%` } } },
  }
  return <Doughnut aria-label="Ringdiagramm: Zielaufteilung des Musterportfolios" role="img" options={options} data={{
    labels: a.map(x => x.name),
    datasets: [{ data: a.map(x => x.anteil), backgroundColor: a.map((_, i) => colors[i % colors.length]), borderColor: t.surface, borderWidth: 2 }],
  }} />
}

// ── Korrelationen: Paare (diverging um 0) und Aktien/Anleihen je Jahr ─────
function KorrPaareChart({ t }: { t: ChartTokens }) {
  const p = kaufplan.korrelation.paare
  const options: ChartOptions<'bar'> = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: {
        label: c => ` Korrelation ${(c.parsed.x ?? 0).toFixed(2)} · ${p[c.dataIndex].fenster}`,
        afterLabel: c => ` Quelle: ${p[c.dataIndex].quelle}`,
      } },
    },
    scales: {
      x: { min: -1, max: 1, grid: { color: c => (c.tick.value === 0 ? t.border : t.grid) }, border: { display: false },
           ticks: { color: t.text3, font: { size: 11 }, stepSize: 0.5 } },
      y: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
    },
  }
  return <Bar aria-label="Balkendiagramm: Korrelation je Anlagepaar von minus 1 bis plus 1" role="img" options={options} data={{
    labels: p.map(x => x.paar),
    datasets: [{ data: p.map(x => x.wert), backgroundColor: p.map(x => (x.wert >= 0 ? t.s1 : t.s2)), borderRadius: 4, barThickness: 16 }],
  }} />
}

function AktienAnleihenChart({ t }: { t: ChartTokens }) {
  const d = kaufplan.korrelation.aktienAnleihen
  const options: ChartOptions<'bar'> = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => ` Korrelation ${(c.parsed.y ?? 0).toFixed(2)}` } } },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: t.text2, font: { size: 11 } } },
      y: { min: -0.6, max: 0.6, grid: { color: c => (c.tick.value === 0 ? t.border : t.grid) }, border: { display: false },
           ticks: { color: t.text3, font: { size: 11 }, stepSize: 0.2 } },
    },
  }
  return <Bar aria-label="Balkendiagramm: Korrelation S&P 500 zu 10-jährigen Treasuries je Jahr" role="img" options={options} data={{
    labels: d.map(x => x.jahr),
    datasets: [{ data: d.map(x => x.wert), backgroundColor: d.map(x => (x.wert >= 0 ? t.s1 : t.s2)), borderRadius: 4, barPercentage: 0.6 }],
  }} />
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function Kaufplan() {
  const t = useChartTokens()
  const k = kaufplan
  const scoreColor = k.tacheles.score < 40 ? 'var(--neg)' : k.tacheles.score < 60 ? 'var(--warn)' : 'var(--pos)'
  const etfs = k.einstiege.filter(e => e.typ === 'etf')
  const top = k.einstiege.filter(e => e.urteil === 'kaufen' || e.urteil === 'staffeln')
  const meiden = k.einstiege.filter(e => e.urteil === 'meiden')
  const totalTranchen = k.tranchen.reduce((s, x) => s + x.anteil, 0)

  return (
    <main className="max-w-[1200px] mx-auto px-4 sm:px-5 py-8">
      {/* ── TACHELES ── */}
      <header className="card-hi p-5 sm:p-7">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="font-mono text-[10px] font-bold tracking-[0.18em] text-accent">KAUFPLAN · TACHELES</span>
          <span className="font-mono text-[10px] text-t3 tabular-nums">Stand {new Date(k.tacheles.stand).toLocaleDateString('de-CH')}</span>
        </div>
        <div className="grid gap-6 md:grid-cols-[1fr_220px] items-start">
          <div>
            <h1 className="text-[26px] sm:text-[32px] font-bold text-t1 leading-tight tracking-tight">{k.tacheles.urteil}</h1>
            <ul className="mt-4 space-y-2 list-none p-0">
              {k.tacheles.punkte.map(p => (
                <li key={p} className="flex gap-2.5 text-[14px] text-t2 leading-relaxed">
                  <span className="text-accent font-bold shrink-0" aria-hidden="true">→</span><span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card p-4 text-center">
            <div className="font-mono text-[10px] text-t3 tracking-widest">MARKT-SCORE</div>
            <div className="font-mono text-[44px] font-bold tabular-nums leading-none mt-2" style={{ color: scoreColor }}>{k.tacheles.score}</div>
            <div className="font-mono text-[11px] font-bold mt-1" style={{ color: scoreColor }}>{k.tacheles.scoreLabel}</div>
            <div className="relative h-2 rounded-full mt-3" style={{ background: 'linear-gradient(to right, var(--neg), var(--warn), var(--pos))' }}
              role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={k.tacheles.score} aria-label="Markt-Score">
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2"
                style={{ left: `${k.tacheles.score}%`, background: 'var(--text-1)', borderColor: 'var(--bg)' }} />
            </div>
            <div className="flex justify-between font-mono text-[9px] text-t3 mt-1.5"><span>Angst</span><span>Gier</span></div>
          </div>
        </div>
      </header>

      {/* ── KENNZAHLEN ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-4">
        {k.kennzahlen.map(z => (
          <div key={z.label} className="card p-3">
            <div className="font-mono text-[10px] text-t3 tracking-wider uppercase">{z.label}</div>
            <div className="font-mono text-[18px] font-bold text-t1 tabular-nums mt-1">{z.wert}</div>
            {z.delta && <div className="font-mono text-[11px] tabular-nums" style={{ color: z.ton === 'pos' ? 'var(--pos)' : z.ton === 'neg' ? 'var(--neg)' : 'var(--text-2)' }}>{z.delta}</div>}
          </div>
        ))}
      </div>

      <nav aria-label="Abschnitte" className="flex flex-wrap gap-2 mt-6 font-mono text-[11px]">
        {[['wann', 'Wann kaufen'], ['wo', 'Wo kaufen'], ['warum', 'Warum jetzt'], ['korrelation', 'Korrelation'], ['portfolio', 'Portfolio'], ['risiken', 'Was den Plan kippt']].map(([id, l]) => (
          <a key={id} href={`#${id}`} className="px-3 py-1.5 rounded-md text-t2 hover:text-t1" style={{ background: 'var(--surface-2)' }}>{l}</a>
        ))}
      </nav>

      {/* ── WANN ── */}
      <Section id="wann" kicker="01 · WANN" title="Gestaffelt kaufen — nicht alles auf einmal"
        lead={`Kapital in ${k.tranchen.length} Tranchen aufteilen. Jede Tranche hat einen klaren Auslöser: Kurslevel oder Ereignis. Wird ein Level nicht erreicht, wird die Tranche zum genannten Datum trotzdem investiert — wer auf den perfekten Boden wartet, verpasst ihn. Einzige Ausnahme: ein klares Bärensignal (siehe T5).`}>
        <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
          <ChartCard title="S&P 500: Kauf-Levels je Tranche" source="eigene Planung, Kurse siehe Stand">
            <TranchenChart t={t} />
          </ChartCard>
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-[13px]">
              <caption className="sr-only">Tranchenplan</caption>
              <thead>
                <tr className="font-mono text-[10px] text-t3 text-left" style={{ background: 'var(--surface-2)' }}>
                  <th scope="col" className="p-3">#</th><th scope="col" className="p-3">Anteil</th><th scope="col" className="p-3">Auslöser → Was</th>
                </tr>
              </thead>
              <tbody>
                {k.tranchen.map(x => (
                  <tr key={x.nr} style={{ borderTop: '1px solid var(--border)' }}>
                    <td className="p-3 align-top font-mono font-bold text-t1">T{x.nr}</td>
                    <td className="p-3 align-top font-mono tabular-nums text-t1">{x.anteil}%</td>
                    <td className="p-3 align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-t1 font-medium">{x.trigger}</span>
                        {x.status === 'jetzt' && <span className="badge font-mono text-[9px] font-bold" style={{ color: 'var(--pos)', background: 'var(--pos-bg)' }}>JETZT</span>}
                        {x.status === 'ausgelöst' && <span className="badge font-mono text-[9px] font-bold" style={{ color: 'var(--pos)', background: 'var(--pos-bg)' }}>AUSGELÖST</span>}
                      </div>
                      <div className="text-t2 text-[12px] mt-1">{x.was}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid var(--border-hi)' }}>
                  <td className="p-3 font-mono text-[11px] text-t3">Σ</td>
                  <td className="p-3 font-mono font-bold tabular-nums text-t1">{totalTranchen}%</td>
                  <td className="p-3 text-[12px] text-t3">Sparplan (monatlich) läuft unabhängig davon weiter.</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </Section>

      {/* ── WO ── */}
      <Section id="wo" kicker="02 · WO" title="Wo die Rabatte liegen — und wo nicht"
        lead="Abstand vom 52-Wochen-Hoch je ETF. Grösster Rabatt heisst nicht automatisch bester Kauf: Farbe = unser Urteil nach Bewertung, Trend und Makro.">
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <ChartCard title="ETFs: Abstand vom 52W-Hoch" source="Kurse lt. Stand, eigene Berechnung" height={Math.max(300, etfs.length * 26)}
            legend={
              <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-3 list-none p-0 font-mono text-[10px] text-t2">
                {(['kaufen', 'staffeln', 'warten', 'meiden'] as const).map(u => (
                  <li key={u} className="flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm" aria-hidden="true"
                      style={{ background: { kaufen: t.pos, staffeln: t.s1, warten: t.warn, meiden: t.neg }[u] }} />
                    {URTEIL_STYLE[u].label}
                  </li>
                ))}
              </ul>
            }>
            <DrawdownChart t={t} rows={etfs} />
          </ChartCard>
          <div className="flex flex-col gap-3">
            <div className="card p-4">
              <h3 className="font-mono text-[11px] font-bold tracking-wider" style={{ color: 'var(--pos)' }}>▲ KAUFEN / STAFFELN</h3>
              <ul className="mt-2 space-y-2 list-none p-0">
                {top.slice(0, 8).map(e => (
                  <li key={e.name} className="text-[13px]">
                    <span className="text-t1 font-semibold">{e.name}</span> <span className="font-mono text-[11px] text-t3">{e.ticker}</span>
                    <div className="text-t2 text-[12px]">Zone: {e.kaufzone}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-4">
              <h3 className="font-mono text-[11px] font-bold tracking-wider" style={{ color: 'var(--neg)' }}>▼ FINGER WEG</h3>
              <ul className="mt-2 space-y-2 list-none p-0">
                {meiden.map(e => (
                  <li key={e.name} className="text-[13px]">
                    <span className="text-t1 font-semibold">{e.name}</span> <span className="text-t2 text-[12px]">— {e.warum}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/einstiege" className="card p-4 text-[13px] font-semibold text-accent hover:underline">
              Alle {k.einstiege.length} Einstiege mit Kaufzone & Stop-Level →
            </Link>
          </div>
        </div>
      </Section>

      {/* ── WARUM ── */}
      <Section id="warum" kicker="03 · WARUM JETZT" title="Die Statistik spricht fürs Einsteigen in Schwächephasen"
        lead="Kaufen nach Rücksetzern war historisch fast immer richtig — aber nur mit Zeithorizont. Kurzfristig kann es weiter runtergehen.">
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title={k.nachKorrektur.titel} source={k.nachKorrektur.quelle} height={300}>
            <ForwardChart t={t} />
          </ChartCard>
          <ChartCard title={k.saison.titel} source={k.saison.quelle}>
            <SaisonChart t={t} />
          </ChartCard>
        </div>
        <div className="grid gap-4 lg:grid-cols-2 mt-4">
          <ChartCard title="Equity Risk Premium: Gewinnrendite S&P 500 minus 10J-Treasury (Prozentpunkte)" source={k.erp.quelle}>
            <ErpChart t={t} />
          </ChartCard>
          <div className="card p-5 text-[14px] text-t2 leading-relaxed space-y-3">
            <h3 className="text-[15px] font-semibold text-t1">Was das heisst</h3>
            {k.nachKorrektur.daten.map(d => (
              <p key={d.label}><span className="text-t1 font-medium">{d.label}:</span> Ø {pct(d.rendite12)} nach 12 Monaten, in {d.positiv12}% der Fälle positiv.</p>
            ))}
            <p><span className="text-t1 font-medium">Aber:</span> Die Risikoprämie liegt nahe null. Aktien zahlen kaum mehr als 10-jährige Staatsanleihen, und Midterm-Jahre bringen im Schnitt einen maximalen Rückgang von −17.5%. Deshalb US-Grosskaps nicht übergewichten. Den Cash-Puffer in kurzen Anleihen parken, bis die Tranchen auslösen.</p>
          </div>
        </div>
      </Section>

      {/* ── KORRELATION ── */}
      <Section id="korrelation" kicker="04 · KORRELATION" title="Was wirklich diversifiziert"
        lead={k.korrelation.hinweis}>
        <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <ChartCard title="Korrelation je Anlagepaar (−1 = gegenläufig, +1 = gleichlaufend)" source="je Balken im Tooltip (Fenster und Quelle unterschiedlich)" height={Math.max(260, k.korrelation.paare.length * 30)}>
            <KorrPaareChart t={t} />
          </ChartCard>
          <ChartCard title="S&P 500 vs. 10J-Treasuries: Korrelation je Jahr" source={k.korrelation.aaQuelle}>
            <AktienAnleihenChart t={t} />
          </ChartCard>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3 font-mono text-[10px] text-t3">
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: t.s2 }} aria-hidden="true" /> negativ = schützt im Abverkauf</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: t.s1 }} aria-hidden="true" /> positiv = fällt mit</span>
        </div>
      </Section>

      {/* ── PORTFOLIO ── */}
      <Section id="portfolio" kicker="05 · PORTFOLIO" title="Zielaufteilung nach allen Tranchen"
        lead="Musterportfolio für einen langfristigen Anleger mit mittlerem Risiko. Keine Einzelaktie über 5%.">
        <div className="grid gap-4 md:grid-cols-[280px_1fr] items-center card p-4 sm:p-5">
          <div style={{ height: 240 }} className="relative">
            <AllokationChart t={t} />
          </div>
          <ul className="list-none p-0 space-y-2.5">
            {k.allokation.map((a, i) => (
              <li key={a.name} className="flex gap-3 items-baseline text-[13px]">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0 translate-y-0.5" aria-hidden="true"
                  style={{ background: [t.s1, t.s2, t.s3, t.s4, t.s5, t.s7][i % 6] }} />
                <span className="font-mono font-bold tabular-nums text-t1 w-10 shrink-0">{a.anteil}%</span>
                <span><span className="text-t1 font-semibold">{a.name}</span> <span className="text-t2">— {a.inhalt}</span></span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* ── RISIKEN ── */}
      <Section id="risiken" kicker="06 · RISIKEN" title="Was den Plan kippt">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {k.risiken.map(r => (
            <div key={r.titel} className="card p-4">
              <h3 className="text-[14px] font-semibold text-t1">{r.titel}</h3>
              <p className="text-[13px] text-t2 mt-1.5 leading-relaxed">{r.text}</p>
              <p className="font-mono text-[11px] mt-2" style={{ color: 'var(--warn)' }}>Signal: {r.signal}</p>
            </div>
          ))}
        </div>
        <div className="card p-4 mt-4">
          <h3 className="font-mono text-[11px] font-bold tracking-wider text-t2 mb-2">TERMINE, DIE BEWEGEN</h3>
          <ul className="list-none p-0 divide-y" style={{ borderColor: 'var(--border)' }}>
            {k.termine.map(x => (
              <li key={x.datum + x.label} className="py-2 flex gap-3 text-[13px]" style={{ borderColor: 'var(--border)' }}>
                <span className="font-mono tabular-nums text-t3 w-[86px] shrink-0">{new Date(x.datum).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}</span>
                <span className={x.wichtig ? 'text-t1 font-medium' : 'text-t2'}>{x.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <details className="mt-10 card p-4">
        <summary className="font-mono text-[11px] text-t2 cursor-pointer">Quellen ({k.quellen.length})</summary>
        <ul className="mt-3 text-[12px] text-t3 space-y-1 list-disc pl-5">
          {k.quellen.map(q => <li key={q}>{q}</li>)}
        </ul>
      </details>
    </main>
  )
}
